/**
 * JWT Token Decoder Utility
 * Decodes JWT tokens client-side to extract claims (no secret key required)
 */

export interface JwtPayload {
	jti?: string;
	iss?: string;
	sub?: string; // Stellar wallet address
	iat?: number;
	exp?: number;
	client_domain?: string;
	client_name?: string; // Client name for v3 API detection
	[key: string]: unknown;
}

export interface DecodedJwt {
	header: Record<string, unknown>;
	payload: JwtPayload;
	signature: string;
}

/**
 * Decodes a JWT token and returns the parsed header, payload, and signature
 * Note: This only decodes the token, it does NOT verify the signature
 *
 * @param token - The JWT token to decode
 * @param silent - If true, suppresses error logging (useful when checking token type)
 */
export function decodeJwt(token: string, silent = false): DecodedJwt | null {
	try {
		// JWT format: header.payload.signature
		const parts = token.split(".");

		if (parts.length !== 3) {
			if (!silent) {
				console.error("Invalid JWT format: expected 3 parts separated by '.'");
			}
			return null;
		}

		const [headerB64, payloadB64, signature] = parts;

		// Decode header
		const header = JSON.parse(atob(headerB64)) as Record<string, unknown>;

		// Decode payload
		const payload = JSON.parse(atob(payloadB64)) as JwtPayload;

		return {
			header,
			payload,
			signature,
		};
	} catch (error) {
		if (!silent) {
			console.error("Failed to decode JWT token:", error);
		}
		return null;
	}
}

/**
 * Extracts the wallet address from the JWT token's 'sub' claim
 * Returns null if the token is invalid or doesn't contain a 'sub' claim
 */
export function extractWalletAddressFromJwt(token: string): string | null {
	const decoded = decodeJwt(token);

	if (!decoded) {
		return null;
	}

	const walletAddress = decoded.payload.sub;

	if (!walletAddress || typeof walletAddress !== "string") {
		console.error("JWT token does not contain a valid 'sub' claim");
		return null;
	}

	return walletAddress;
}

/**
 * Extracts the client name from the JWT token's 'client_name' claim
 * Returns null if the token is invalid or doesn't contain a 'client_name' claim
 * Used for v3 API detection
 */
export function extractClientNameFromJwt(token: string): string | null {
	const decoded = decodeJwt(token);

	if (!decoded) {
		return null;
	}

	const clientName = decoded.payload.client_name;

	if (!clientName || typeof clientName !== "string") {
		return null;
	}

	return clientName;
}

/**
 * Checks if a JWT token is expired based on the 'exp' claim
 * Returns true if expired or if 'exp' claim is missing
 */
export function isJwtExpired(token: string): boolean {
	const decoded = decodeJwt(token);

	if (!decoded || !decoded.payload.exp) {
		return true;
	}

	const expirationTime = decoded.payload.exp * 1000; // Convert to milliseconds
	const currentTime = Date.now();

	return currentTime >= expirationTime;
}

/**
 * Checks if a token has JWT format (3 parts separated by dots)
 * This is a fast check that doesn't attempt to decode the token
 */
function hasJwtFormat(token: string): boolean {
	return token.split(".").length === 3;
}

/**
 * Determines if a token is a JWT or OAuth token
 * Returns the token type: 'jwt' | 'oauth' | 'unknown'
 *
 * JWT tokens have a specific format: header.payload.signature (3 parts)
 * OAuth tokens are typically opaque strings without this structure
 */
export function getTokenType(token: string): "jwt" | "oauth" | "unknown" {
	if (!token || token.trim() === "") {
		return "unknown";
	}

	// First, check if it has JWT format (3 parts separated by dots)
	// This avoids unnecessary decoding attempts for OAuth tokens
	if (!hasJwtFormat(token)) {
		// Not JWT format, likely OAuth token
		return "oauth";
	}

	// Has JWT format, try to decode to verify it's a valid JWT
	// Use silent mode to avoid logging errors for OAuth tokens that happen to have dots
	const decoded = decodeJwt(token, true);

	// If it successfully decodes and has expected JWT structure (sub claim for wallet address)
	if (decoded?.payload.sub) {
		return "jwt";
	}

	// Has JWT format but couldn't decode properly, could be malformed or OAuth-like
	// Treat as OAuth to be safe
	return "oauth";
}

/**
 * Determines the appropriate API version based on token type
 * JWT tokens → v3 API by default (wallet address in token, context from prepare endpoint)
 *              v2 API if context params present in URL (handled by getApiVersionFromSearchParams)
 * OAuth tokens → v5 API (wallet address as query parameter)
 *
 * Note: This function only determines version from token type.
 * For full detection including URL context params, use getApiVersionFromSearchParams.
 */
export function getApiVersionFromToken(token: string): "v2" | "v3" | "v5" {
	const tokenType = getTokenType(token);

	if (tokenType === "oauth") {
		return "v5";
	}

	// Default to v3 for JWT and unknown tokens
	// v2 detection requires URL context params (handled by getApiVersionFromSearchParams)
	return "v3";
}
